<template>
  <div class="provider-response-forwarding">
    <div class="text-body-2 mb-1 d-flex align-center flex-wrap ga-2">
      <span>Default communication method with prescribing provider</span>
      <v-chip v-if="locked" size="small" variant="tonal" color="info" label prepend-icon="mdi-account-check-outline">
        Set by the provider on their account
      </v-chip>
    </div>
    <v-radio-group
      :model-value="locked ? registeredPreference : enabled ? 'email' : 'fax'"
      :disabled="disabled || locked"
      :readonly="readonly"
      hide-details
      density="compact"
      inline
      class="mb-1"
      @update:model-value="onMethodChange">
      <v-radio label="Fax" value="fax" />
      <v-radio label="Email" value="email" />
    </v-radio-group>
    <div v-if="locked" class="text-caption text-medium-emphasis mb-2">
      This provider has a Do Prior Auth login and asked to be reached by {{ registeredPreference }}. That choice is used
      when sending to them.
    </div>
    <v-text-field
      v-if="enabled"
      :model-value="email"
      label="Provider email"
      placeholder="name@clinic.com"
      type="email"
      class="lh--force-white-background"
      :readonly="readonly"
      :disabled="disabled"
      :error-messages="emailErrorMessages"
      @update:model-value="onEmailChange" />
  </div>
</template>

<script setup>
import { computed } from "vue";
import { isEmailValid } from "@/utils/validators";

const props = defineProps({
  enabled: { type: Boolean, default: false },
  email: { type: String, default: null },
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  showErrors: { type: Boolean, default: false },
  // The registered provider login's own choice (LAM-5508). When set, the method follows it and the radios
  // are disabled (readonly leaves native radios clickable): LAM-5263 already sends by this channel
  // regardless of what a tenant picks here. The email typed below stays the tenant's fallback — a send
  // to a registered login goes to its login email, which is never shown here.
  registeredPreference: { type: String, default: null },
});

const emit = defineEmits(["update:enabled", "update:email"]);

const locked = computed(() => props.registeredPreference === "email" || props.registeredPreference === "fax");

const emailErrorMessages = computed(() => {
  if (!props.showErrors || !props.enabled) return null;
  if (!props.email || !String(props.email).trim()) {
    return "Provider email is required when forwarding responses.";
  }
  if (!isEmailValid(props.email)) {
    return "Please enter a valid email address.";
  }
  return null;
});

function onMethodChange(value) {
  if (locked.value) return;
  emit("update:enabled", value === "email");
}

function onEmailChange(value) {
  emit("update:email", value);
}

function validate() {
  if (!props.enabled) return true;
  return Boolean(props.email && String(props.email).trim() && isEmailValid(props.email));
}

defineExpose({ validate });
</script>
